import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  CheckCircle2,
  Loader2,
  XCircle,
  FileText,
  ArrowRight,
  Users,
  Share2,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { confetti } from 'dom-confetti';
import { track } from '../utils/track';
import { formatIndustryLabel } from '../utils/reportHelpers';

const API_BASE =
  process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verifying your payment...');
  const [tenantConfig, setTenantConfig] = useState(null);
  const [reportData, setReportData] = useState(null);
  const pollRef = useRef();

  const sessionId = searchParams.get('session_id');
  const reportId = searchParams.get('report_id');
  // Guard the purchase event so it fires AT MOST ONCE per page load.
  // Without this, a user navigating away and back, or a stray re-render,
  // could double-count a conversion in Google Ads / GA4 — which would
  // poison Smart Bidding's CPA optimization with phantom revenue.
  const purchaseTrackedRef = useRef(false);

  const stopPolling = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (!sessionId || !reportId) {
      setStatus('error');
      setMessage('Invalid payment session.');
      return;
    }

    let attempts = 0;
    const maxAttempts = 45; // 45 × 2s = 90s — Stripe webhooks can take 30–60s

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/payment/status/${sessionId}?report_id=${reportId}`
        );

        if (!response.ok) throw new Error('Failed to verify');

        const data = await response.json();

        if (data.isPaid) {
          stopPolling();
          setStatus('success');
          setMessage('🎉 Payment successful! Your AI Blueprint is ready.');

          const reportRes = await fetch(`${API_BASE}/api/audit/report/${reportId}`);
          let _reportPrice = 149;
          let _reportIndustry = 'Custom';
          if (reportRes.ok) {
            const reportJson = await reportRes.json();
            setReportData(reportJson);
            setTenantConfig(reportJson.tenantConfig || null);
            // Prefer the actual amount from the payment status response,
            // fall back to report-level pricing, then headline $149.
            // Stripe returns amount_total in cents; we store dollars elsewhere.
            _reportPrice =
              (typeof data.amount_total === 'number' && data.amount_total > 0
                ? data.amount_total / 100
                : null)
              ?? (typeof reportJson.price === 'number' ? reportJson.price : null)
              ?? 149;
            _reportIndustry = reportJson.industry || 'Custom';
          }

          // GA4 ecommerce: purchase — fires exactly once per page load.
          // This is THE event Google Ads optimizes against. transaction_id
          // = Stripe session id (idempotent dedupe key); value is the real
          // dollars charged. Until this lands, every $50 of ad spend buys
          // raw clicks with zero learning — Smart Bidding has nothing to
          // optimize for.
          if (!purchaseTrackedRef.current) {
            purchaseTrackedRef.current = true;
            track('purchase', {
              transaction_id: sessionId,
              currency: 'USD',
              value: _reportPrice,
              items: [{
                item_id: reportId,
                item_name: `AI Implementation Blueprint - ${_reportIndustry}`,
                item_category: 'AI Blueprint',
                price: _reportPrice,
                quantity: 1,
              }],
            });
          }

          setTimeout(() => {
            if (typeof confetti !== 'undefined') {
              confetti(document.body, {
                angle: 90,
                spread: 360,
                startVelocity: 30,
                elementCount: 150,
                dragFriction: 0.12,
                duration: 3000,
                colors: ['#3B82F6', '#06B6D4', '#10B981'],
              });
            }
          }, 500);
          return;
        }

        if (data.status === 'expired') {
          stopPolling();
          setStatus('expired');
          setMessage('Payment session expired.');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          pollRef.current = setTimeout(checkPaymentStatus, 2000);
        } else {
          stopPolling();
          setStatus('error');
          setMessage(
            'Payment verification timed out. Please check your email for confirmation.'
          );
        }
      } catch (error) {
        console.error('Payment status check error:', error);
        attempts++;
        if (attempts < 5) {
          pollRef.current = setTimeout(checkPaymentStatus, 2000);
        } else {
          stopPolling();
          setStatus('error');
          setMessage(
            'Unable to verify payment. If your card was charged, please check your email.'
          );
        }
      }
    };

    pollRef.current = setTimeout(checkPaymentStatus, 1000);

    return stopPolling;
  }, [sessionId, reportId]);

  const handleShare = () => {
    const brand = tenantConfig?.brandName || 'AI Workshop LLC';
    const shareText = `Just got my custom AI Implementation Blueprint from ${brand} – check it out: ${window.location.origin}/full-report/${reportId}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareText);
      toast.success('Share link copied!');
    } else {
      toast.info(shareText);
    }
  };

  const businessName = reportData?.businessName || 'your business';
  const industry = reportData?.industry;
  const subVertical = reportData?.subVertical;

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center max-w-md mx-auto">
            <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-2xl flex items-center justify-center shadow-lg border border-blue-700/30">
              <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Just a moment...</h2>
            <p className="text-xl text-slate-300 mb-2">{message}</p>
            <p className="text-sm text-slate-400">Finalizing your AI Implementation Blueprint</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-28 h-28 mx-auto mb-8 bg-gradient-to-br from-blue-900/30 to-teal-900/30 rounded-3xl flex items-center justify-center shadow-2xl border-4 border-blue-700/30">
              <CheckCircle2 className="w-16 h-16 text-[#3b82f6]" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Payment Successful</h2>
            <p className="text-xl text-slate-300 mb-2">
              Your custom <span className="font-semibold text-white">AI Implementation Blueprint</span>{' '}
              for {businessName} is ready.
            </p>
            {industry && (
              <p className="text-sm text-slate-400 mb-8">
                Tailored for {formatIndustryLabel(industry, 'your business', subVertical)} using your answers, tools, and goals.
              </p>
            )}

            <div className="grid md:grid-cols-3 gap-6 mb-10 text-left">
              <Card className="bg-slate-950/50 border border-white/10 shadow-lg">
                <CardContent className="p-6">
                  <FileText className="w-8 h-8 text-blue-400 mb-3" />
                  <h3 className="font-bold text-lg mb-1 text-white">Web Blueprint</h3>
                  <p className="text-sm text-slate-400">
                    Interactive web version with roadmap, tools, prompts, ROI, and risks.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-slate-950/50 border border-white/10 shadow-lg">
                <CardContent className="p-6">
                  <FileText className="w-8 h-8 text-[#3b82f6] mb-3" />
                  <h3 className="font-bold text-lg mb-1 text-white">PDF Download</h3>
                  <p className="text-sm text-slate-400">
                    Shareable PDF of your AI Implementation Blueprint, perfect for teams and advisors.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-slate-950/50 border border-white/10 shadow-lg">
                <CardContent className="p-6">
                  <Mail className="w-8 h-8 text-amber-300 mb-3" />
                  <h3 className="font-bold text-lg mb-1 text-white">Email Confirmation</h3>
                  <p className="text-sm text-slate-400">
                    We’ve emailed your receipt and a link to revisit your blueprint anytime.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    We’ll check in at the 90-day mark to hear how AI has changed things for {businessName}.
                  </p>
                </CardContent>
              </Card>
            </div>

            <Button
              onClick={() => navigate(`/full-report/${reportId}?just_unlocked=true`)}
              size="lg"
              className="bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold text-xl px-12 py-8 shadow-2xl shadow-blue-500/20 rounded-full mb-6 hover:scale-105 hover:shadow-blue-500/40 transition-all"
            >
              <FileText className="w-6 h-6 mr-2" />
              View My AI Blueprint
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm text-slate-400">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="hover:text-[#3b82f6]"
              >
                <Users className="w-4 h-4 mr-1" />
                Go to Dashboard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                className="hover:text-[#3b82f6]"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share that you started your AI plan
              </Button>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center max-w-md mx-auto">
            <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-amber-900/30 to-orange-900/30 rounded-2xl flex items-center justify-center shadow-lg border border-amber-700/30">
              <XCircle className="w-12 h-12 text-amber-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Session Expired</h2>
            <p className="text-xl text-slate-300 mb-8">{message}</p>

            <Button
              onClick={() => navigate(`/payment/${reportId}`)}
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold"
            >
              Complete Purchase
            </Button>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center max-w-md mx-auto">
            <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-red-900/30 to-rose-900/30 rounded-2xl flex items-center justify-center shadow-lg border border-red-700/30">
              <XCircle className="w-12 h-12 text-red-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Something Went Wrong</h2>
            <p className="text-xl text-slate-300 mb-8">{message}</p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate(`/payment/${reportId}`)}
                variant="outline"
                className="flex-1 border-white/15 text-slate-300 hover:bg-white/8 rounded-full"
              >
                Try Payment Again
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="secondary"
                className="flex-1 bg-slate-900/70 hover:bg-white/8 text-white rounded-full"
              >
                Retry Verification
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] font-sans text-white">
      <Header />

      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border border-white/10 shadow-2xl bg-slate-900/70 backdrop-blur-xl overflow-hidden">
            <CardContent className="p-8 lg:p-12">{renderContent()}</CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
